import { useEffect, useMemo, useRef, useState } from 'react';
import { TextInput, UnstyledButton } from '@mantine/core';
import type { Scene } from '@nelnela/contracts';
import { fmtTime } from '../../lib/text';
import {
  autoSceneName,
  clampSceneSpan,
  msAtFraction,
  pct,
  sceneRange,
  sortScenes,
} from '../../lib/scenes';

export interface ScenesBarProps {
  scenes: Scene[];
  durationMs: number | null;
  currentMs: number;
  pendingIn: number | null;
  pendingOut: number | null;
  /** which scene's loop is active (engine loop label === scene.id) */
  loopingSceneId: string | null;
  /** sceneCreate is entitled; "+ scene" is disabled with the calm line otherwise */
  entitled: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** timeline tap → seek */
  onSeek: (ms: number) => void;
  /** set pending in/out at the playhead (the caller reads currentMs) */
  onMarkIn: () => void;
  onMarkOut: () => void;
  onClearPending: () => void;
  /** create from pendingIn/pendingOut (the caller clamps + calls the API); never '' */
  onAdd: (name: string) => void;
  /** seek to scene.startMs and play */
  onJump: (scene: Scene) => void;
  onLoop: (scene: Scene) => void;
  /** M6a step 8: open the vocabulary preview for this scene's span. Absent = no affordance. */
  onWords?: (scene: Scene) => void;
  onStopLoop: () => void;
  onRename: (scene: Scene, name: string) => void;
  onDelete: (scene: Scene) => void;
}

/** Component-local classes (theme vars only). Every target ≥ 40 px tall (F4 house rule). */
const SCENES_CSS = `
.nn-sc-label { font-size: 10px; color: var(--nn-faint); letter-spacing: .03em; white-space: nowrap; }
.nn-sc-tl { position: relative; flex: 1; min-width: 0; }
.nn-sc-tl[data-seek] { cursor: pointer; }
.nn-sc-track { position: absolute; left: 0; right: 0; top: 50%; height: 3px; transform: translateY(-50%);
  border-radius: 2px; background: var(--nn-hairline); }
.nn-sc-span { position: absolute; top: 0; bottom: 0; border-radius: 2px;
  background: color-mix(in oklab, var(--nn-accent) 35%, transparent); }
.nn-sc-mark { position: absolute; top: 50%; transform: translate(-50%, -50%); border-radius: 1px; }
.nn-sc-mark--pending { width: 2px; height: 12px; background: var(--nn-accent); }
.nn-sc-mark--head { width: 1.5px; height: 8px; background: var(--nn-text); }
.nn-sc-controls { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.nn-sc-chip { display: inline-flex; align-items: center; justify-content: center; min-height: 40px;
  min-width: 40px; padding: 0 10px; border-radius: 999px; border: 1px solid var(--nn-hairline);
  color: var(--nn-muted); font-size: 11px; white-space: nowrap; }
.nn-sc-chip[data-primary] { color: var(--nn-text); }
.nn-sc-chip[data-active] { color: var(--nn-accent-text);
  border-color: color-mix(in oklab, var(--nn-accent) 50%, transparent); }
.nn-sc-chip[data-danger] { color: light-dark(#b0433b, #e0938c); }
.nn-sc-chip:disabled { opacity: .4; cursor: default; }
.nn-sc-row { display: flex; align-items: center; gap: 6px; min-height: 40px; padding: 0 4px 0 10px;
  border-radius: 8px; border: 1px solid var(--nn-hairline); }
.nn-sc-row[data-looping] { background: color-mix(in oklab, var(--nn-accent) 8%, transparent); }
.nn-sc-jump { flex: 1; min-width: 0; min-height: 40px; display: flex; align-items: center; gap: 8px; }
.nn-sc-name { font-size: 12.5px; color: var(--nn-muted); overflow: hidden; text-overflow: ellipsis;
  white-space: nowrap; }
.nn-sc-row[data-looping] .nn-sc-name { color: var(--nn-text); }
.nn-sc-range { font-size: 10.5px; color: var(--nn-faint); font-variant-numeric: tabular-nums; flex-shrink: 0; }
`;

/**
 * F4 scenes bar (frame 12a): collapsed = a 28 px strip (mini timeline + chevron); expanded =
 * timeline (tap to seek; spans in ember; pending in/out; playhead) · mark in/out + name + add ·
 * the scene rows (jump · loop/stop · rename inline · delete with 2-tap confirm). No API or
 * engine access — everything via props; maths from `lib/scenes`.
 */
export function ScenesBar(props: ScenesBarProps): JSX.Element {
  const { scenes, durationMs, currentMs, pendingIn, pendingOut, collapsed } = props;
  const sorted = useMemo(() => sortScenes(scenes), [scenes]);
  const [name, setName] = useState('');
  const canAdd =
    props.entitled &&
    pendingIn != null &&
    pendingOut != null &&
    clampSceneSpan(pendingIn, pendingOut, durationMs) !== null;
  const add = () => {
    if (!canAdd) return;
    props.onAdd(name.trim() || autoSceneName(pendingIn ?? 0));
    setName('');
  };
  const timeline = (h: number) => (
    <Timeline
      scenes={sorted}
      durationMs={durationMs}
      currentMs={currentMs}
      pendingIn={pendingIn}
      pendingOut={pendingOut}
      height={h}
      onSeek={collapsed ? null : props.onSeek}
    />
  );

  if (collapsed) {
    return (
      <>
        <style>{SCENES_CSS}</style>
        <UnstyledButton
          onClick={props.onToggleCollapsed}
          aria-label="Show scenes"
          aria-expanded={false}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%', // a <button> is shrink-to-fit by default; the strip spans the row
            height: 28,
            padding: '0 14px',
          }}
        >
          <span className="nn-sc-label">scenes ▾</span>
          {timeline(28)}
          <span className="nn-sc-label">{scenes.length || ''}</span>
        </UnstyledButton>
      </>
    );
  }

  return (
    <div style={{ padding: '4px 14px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <style>{SCENES_CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UnstyledButton
          onClick={props.onToggleCollapsed}
          aria-label="Hide scenes"
          aria-expanded
          style={{ height: 40, display: 'flex', alignItems: 'center', paddingRight: 4 }}
        >
          <span className="nn-sc-label">scenes ▴</span>
        </UnstyledButton>
        {timeline(40)}
      </div>

      <div className="nn-sc-controls">
        <Chip
          onClick={props.onMarkIn}
          title="mark scene start at the playhead (i)"
          active={pendingIn != null}
        >
          ⇥ in{pendingIn != null && <b style={{ marginLeft: 4 }}>{fmtTime(pendingIn)}</b>}
        </Chip>
        <Chip
          onClick={props.onMarkOut}
          title="mark scene end at the playhead (o)"
          active={pendingOut != null}
        >
          ⇤ out{pendingOut != null && <b style={{ marginLeft: 4 }}>{fmtTime(pendingOut)}</b>}
        </Chip>
        <TextInput
          size="sm"
          aria-label="Scene name"
          placeholder={autoSceneName(pendingIn ?? currentMs)}
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.currentTarget.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          style={{ flex: 1, minWidth: 120 }}
        />
        <Chip onClick={add} disabled={!canAdd} primary title="add this scene">
          ＋ scene
        </Chip>
        {(pendingIn != null || pendingOut != null) && (
          <Chip onClick={props.onClearPending} title="clear the pending marks">
            clear
          </Chip>
        )}
      </div>
      {!props.entitled && <span className="nn-sc-label">Scenes are part of the paid layer.</span>}

      {sorted.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {sorted.map((sc) => (
            <SceneRow key={sc.id} scene={sc} looping={props.loopingSceneId === sc.id} {...props} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- pieces ---------------------------------------------------------------------

function Timeline(p: {
  scenes: Scene[];
  durationMs: number | null;
  currentMs: number;
  pendingIn: number | null;
  pendingOut: number | null;
  height: number;
  onSeek: ((ms: number) => void) | null;
}) {
  const at = (ms: number) => `${pct(ms, p.durationMs)}%`;
  return (
    <div
      className="nn-sc-tl"
      data-seek={p.onSeek ? '' : undefined}
      role={p.onSeek ? 'slider' : undefined}
      aria-label={p.onSeek ? 'timeline — tap to seek' : undefined}
      aria-valuemin={0}
      aria-valuemax={p.durationMs ?? 0}
      aria-valuenow={Math.round(p.currentMs)}
      style={{ height: p.height }}
      onClick={(e) => {
        if (!p.onSeek) return;
        const r = e.currentTarget.getBoundingClientRect();
        const ms = msAtFraction((e.clientX - r.left) / r.width, p.durationMs);
        if (ms != null) p.onSeek(ms);
      }}
    >
      <div className="nn-sc-track">
        {p.scenes.map((sc) => (
          <span
            key={sc.id}
            className="nn-sc-span"
            title={sc.name}
            style={{
              left: at(sc.startMs),
              width: `${Math.max(0.5, pct(sc.endMs, p.durationMs) - pct(sc.startMs, p.durationMs))}%`,
            }}
          />
        ))}
      </div>
      {p.pendingIn != null && (
        <span
          aria-hidden
          className="nn-sc-mark nn-sc-mark--pending"
          style={{ left: at(p.pendingIn) }}
        />
      )}
      {p.pendingOut != null && (
        <span
          aria-hidden
          className="nn-sc-mark nn-sc-mark--pending"
          style={{ left: at(p.pendingOut) }}
        />
      )}
      <span aria-hidden className="nn-sc-mark nn-sc-mark--head" style={{ left: at(p.currentMs) }} />
    </div>
  );
}

function SceneRow({
  scene,
  looping,
  onJump,
  onLoop,
  onWords,
  onStopLoop,
  onRename,
  onDelete,
}: Pick<
  ScenesBarProps,
  'onJump' | 'onLoop' | 'onWords' | 'onStopLoop' | 'onRename' | 'onDelete'
> & {
  scene: Scene;
  looping: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null); // rename in progress
  const closingRef = useRef(false); // Enter/Esc already handled this edit — ignore the blur that follows
  const [armed, setArmed] = useState(false); // "delete?" shown; disarms after 3 s
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);
  const close = (save: boolean) => {
    if (closingRef.current) return;
    closingRef.current = true;
    const n = draft?.trim() ?? '';
    if (save && n && n !== scene.name) onRename(scene, n);
    setDraft(null);
  };
  const startRename = () => {
    closingRef.current = false;
    setDraft(scene.name);
  };
  return (
    <div className="nn-sc-row" data-looping={looping ? '' : undefined}>
      {draft !== null ? (
        <TextInput
          size="sm"
          autoFocus
          aria-label="Rename scene"
          value={draft}
          maxLength={80}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={() => close(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') close(true);
            else if (e.key === 'Escape') close(false);
          }}
          style={{ flex: 1, minWidth: 0 }}
        />
      ) : (
        <UnstyledButton
          className="nn-sc-jump"
          onClick={() => onJump(scene)}
          title={`jump to ${sceneRange(scene)}`}
        >
          <span className="nn-sc-name">{scene.name}</span>
          <span className="nn-sc-range">{sceneRange(scene)}</span>
        </UnstyledButton>
      )}
      {/* M6a: what is IN this scene, before watching it. Opening the sheet emits nothing — a
          list of words is not reading in context. */}
      {onWords && (
        <Chip onClick={() => onWords(scene)} title="words in this scene">
          words
        </Chip>
      )}
      {looping ? (
        <Chip onClick={onStopLoop} title="stop looping" active>
          ■ loop
        </Chip>
      ) : (
        <Chip onClick={() => onLoop(scene)} title="loop this scene">
          ⟳ loop
        </Chip>
      )}
      <Chip onClick={startRename} title="rename" aria-label="Rename">
        ✎
      </Chip>
      {armed ? (
        <Chip onClick={() => onDelete(scene)} title="tap again to delete" danger>
          delete?
        </Chip>
      ) : (
        <Chip onClick={() => setArmed(true)} title="delete scene" aria-label="Delete">
          ✕
        </Chip>
      )}
    </div>
  );
}

function Chip(p: {
  onClick: () => void;
  children: React.ReactNode;
  title?: string;
  'aria-label'?: string;
  disabled?: boolean;
  active?: boolean;
  primary?: boolean;
  danger?: boolean;
}) {
  return (
    <UnstyledButton
      className="nn-sc-chip"
      onClick={p.onClick}
      disabled={p.disabled}
      title={p.title}
      aria-label={p['aria-label']}
      data-active={p.active ? '' : undefined}
      data-primary={p.primary ? '' : undefined}
      data-danger={p.danger ? '' : undefined}
    >
      {p.children}
    </UnstyledButton>
  );
}
